import React, { useCallback, useMemo, useState } from 'react';
import { StyleProp, StyleSheet, ViewStyle } from 'react-native';

import { PlayerProjectionFragment } from '@/api/events/query.generated';
import { EventInfo } from '@/api/events/types/types';
import TilePlaceholderImage from '@/assets/images/tilePlaceholderImage';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { PlayerWithTeam } from '@/feature/betslip-pickem/types';
import { SingleProjectionIcon } from '@/feature/lobby/components/SingleProjectionIcon';
import { PlayerTileLogo } from '@/feature/lobby/components/tile/PlayerTileLogo';
import { common } from '@/styles/styles';
import { League, ProjectionType } from '@/types/api.generated';
import { shouldShowShirtImage } from '@/utils/formatPlayerInfo';
import { isCollegeSport, isPGA, isTennis } from '@/utils/league';
import { isPlayerImgPlaceholder } from '@/utils/player';

import { Rectangle } from '../types';
import { TilePlayerImageCanvas } from './TilePlayerImageCanvas';
import { CollegeBasketballShirt } from './college-sports/CollegeBasketballShirt';
import { CollegeFootballShirt, CollegeFootballShirtBackground } from './college-sports/CollegeFootballShirt';
import { jerseyNumberTopOffset } from './utils';

type Props = {
    playerImageUrl?: string;
    teamImageUrl?: string;
    playerNumber?: number | null;
    style?: StyleProp<ViewStyle>;
    customSize?: Rectangle;
    teamColor?: string;
    bgColor?: 'background.neutral.subtler' | 'background.neutral.subtle';
    containerWidth: number;
    selectedProjection?: PlayerProjectionFragment;
    event?: EventInfo;
    player: PlayerWithTeam;
    secondaryColor?: string;
    logoText?: string;
};

export const TileProfileImage = ({
    playerImageUrl,
    teamImageUrl,
    teamColor,
    bgColor = ds.colorToken.background.neutral.subtler,
    playerNumber,
    containerWidth,
    selectedProjection,
    event,
    player,
    secondaryColor,
    logoText,
}: Props) => {
    const [hasErrorLoadingImage, setHasErrorLoadingImage] = useState(false);
    const isPlaceholderImage = isPlayerImgPlaceholder(playerImageUrl) || hasErrorLoadingImage;

    const teamImageSource = useMemo(() => ({ uri: teamImageUrl }), [teamImageUrl]);

    const onImageError = useCallback(() => {
        setHasErrorLoadingImage(true);
    }, []);
    const isNonRegularProjection = selectedProjection?.type !== ProjectionType.Regular;
    const isLogoTextLeague = isPGA(event?.league) || isCollegeSport(event?.league) || isTennis(event?.league);
    const shouldShowLogoText = isLogoTextLeague && !!logoText;

    const showCollegeShirtImage = shouldShowShirtImage(playerNumber, isPlaceholderImage);

    return (
        <>
            <Box alignItems="center">
                <TilePlayerImageCanvas
                    teamColor={teamColor}
                    bgColor={bgColor}
                    source={playerImageUrl}
                    hideImage={isPlaceholderImage}
                    containerWidth={containerWidth}
                    onError={onImageError}
                />
            </Box>
            <Box
                position={'absolute'}
                style={common.zIndex1}
                top={15}
                left={0}
                width={containerWidth}
                alignItems={'center'}
            >
                {showCollegeShirtImage ? (
                    <>
                        {event?.league === League.Cbb ? (
                            <CollegeBasketballShirt
                                teamColor={teamColor}
                                secondaryColor={secondaryColor}
                                playerId={player.id}
                            />
                        ) : (
                            <Box>
                                <CollegeFootballShirt
                                    teamColor={teamColor}
                                    secondaryColor={secondaryColor}
                                    playerId={player.id}
                                />
                                <Box zIndex={-1} position={'absolute'} top={0}>
                                    <CollegeFootballShirtBackground />
                                </Box>
                            </Box>
                        )}
                        <Box width="100%" alignItems={'center'} position={'absolute'} top={jerseyNumberTopOffset}>
                            <Text style={[styles.playerNumber, styles.textShadow]}>{playerNumber}</Text>
                        </Box>
                    </>
                ) : isPlaceholderImage ? (
                    <Box top={-20}>
                        <TilePlaceholderImage id={player.id} />
                    </Box>
                ) : null}
            </Box>
            <Box top={shouldShowLogoText ? 20 : 16} right={16} position="absolute" style={common.zIndex0}>
                <PlayerTileLogo
                    teamImageSource={teamImageSource}
                    logoText={logoText}
                    shouldShowLogoText={shouldShowLogoText}
                />
            </Box>
            {isNonRegularProjection && selectedProjection ? (
                <Box top={15} left={15} position="absolute" style={common.zIndex0}>
                    <SingleProjectionIcon selectedProjection={selectedProjection} triggerEducationalSheet />
                </Box>
            ) : null}
        </>
    );
};

const styles = StyleSheet.create({
    playerNumber: {
        color: ds.colorValue.text.primary,
        fontFamily: 'Oswald-SemiBold',
        fontWeight: 700,
        fontSize: 18,
        lineHeight: 28,
    },
    textShadow: {
        textShadowColor: 'rgba(0, 0, 0, 0.25)',
        textShadowRadius: 4,
    },
});
